import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Container from "@cloudscape-design/components/container";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import Box from "@cloudscape-design/components/box";
import Header from "@cloudscape-design/components/header";
import Table from "@cloudscape-design/components/table";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Tabs from "@cloudscape-design/components/tabs";
import Alert from "@cloudscape-design/components/alert";
import LineChart from "@cloudscape-design/components/line-chart";
import Select from "@cloudscape-design/components/select";
import { apiFetch } from "./apiFetch";
import { getDatadogCreds } from "./datadogCredentials";

export interface Ec2InstanceData {
  instanceId: string;
  name: string;
  instanceType: string;
  state: string;
  privateIp: string;
  publicIp: string;
  vpcId: string;
  subnetId: string;
  launchTime: string;
  imageId: string;
  keyName: string;
  platform: string;
  architecture: string;
  tags: { Key: string; Value: string }[];
  securityGroups: { GroupId: string; GroupName: string }[];
}

function stateToIndicator(state: string) {
  const map: Record<string, "success" | "error" | "warning" | "stopped" | "info"> = {
    running: "success",
    stopped: "stopped",
    terminated: "error",
    pending: "warning",
    "shutting-down": "warning",
    stopping: "warning",
  };
  return map[state] || "info";
}

const RANGE_OPTIONS = [
  { label: "Last 1 hour", value: "3600" },
  { label: "Last 6 hours", value: "21600" },
  { label: "Last 24 hours", value: "86400" },
];

interface MetricPoint {
  timestamp: number;
  value: number;
}

interface Ec2MetricChartProps {
  instanceId: string;
  metric: string;
  title: string;
  yTitle: string;
  rangeSeconds: number;
  /** Multiplier applied to raw values for display (e.g. 100 for 0–1 fractions). */
  scale?: number;
  creds: { token: string; site: string };
}

function Ec2MetricChart({
  instanceId,
  metric,
  title,
  yTitle,
  rangeSeconds,
  scale = 1,
  creds,
}: Ec2MetricChartProps) {
  const [points, setPoints] = useState<MetricPoint[]>([]);
  const [status, setStatus] = useState<"loading" | "finished" | "error">("loading");
  const [errorText, setErrorText] = useState("");
  const [reloadCounter, setReloadCounter] = useState(0);

  useEffect(() => {
    if (!instanceId) return;
    let cancelled = false;
    setStatus("loading");
    setErrorText("");
    const to = Math.floor(Date.now() / 1000);
    const from = to - rangeSeconds;
    apiFetch(`/metrics/ec2/${instanceId}/${metric}?from=${from}&to=${to}`, {
      headers: { "X-Datadog-Token": creds.token, "X-Datadog-Site": creds.site },
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error(body?.error?.message || `Failed to load metrics (${res.status})`);
        }
        const json = await res.json();
        if (cancelled) return;
        setPoints(Array.isArray(json?.series) ? json.series : []);
        setStatus("finished");
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setErrorText(e.message);
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
    // refetch keyed on instance, metric, range, and manual retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId, metric, rangeSeconds, reloadCounter]);

  return (
    <LineChart
      statusType={status}
      series={[
        {
          title,
          type: "line",
          data: points.map((p) => ({ x: new Date(p.timestamp), y: p.value * scale })),
        },
      ]}
      xScaleType="time"
      xTitle="Time"
      yTitle={yTitle}
      height={250}
      hideFilter
      errorText={errorText || "Error loading metrics"}
      recoveryText="Retry"
      onRecoveryClick={() => setReloadCounter((c) => c + 1)}
      empty={<Box textAlign="center" color="inherit">No data available for this range.</Box>}
      noMatch={<Box textAlign="center" color="inherit">No data available for this range.</Box>}
    />
  );
}

function Ec2Monitoring({ instanceId }: { instanceId: string }) {
  const creds = getDatadogCreds();
  const [rangeSeconds, setRangeSeconds] = useState(3600);

  if (!creds) {
    return (
      <Alert type="info" header="Datadog not configured">
        Configure a Datadog token in <Link to="/settings">Settings</Link> to view metrics.
      </Alert>
    );
  }

  return (
    <SpaceBetween size="m">
      <Select
        selectedOption={RANGE_OPTIONS.find((o) => o.value === String(rangeSeconds)) ?? RANGE_OPTIONS[0]}
        options={RANGE_OPTIONS}
        onChange={({ detail }) => setRangeSeconds(Number(detail.selectedOption.value))}
      />
      <ColumnLayout columns={2}>
        <div key="cpu">
          <Ec2MetricChart
            instanceId={instanceId}
            metric="cpu"
            title="CPU Utilization"
            yTitle="CPU %"
            rangeSeconds={rangeSeconds}
            creds={creds}
          />
        </div>
        <div key="disk">
          <Ec2MetricChart
            instanceId={instanceId}
            metric="disk"
            title="Disk in Use"
            yTitle="Disk in use %"
            rangeSeconds={rangeSeconds}
            scale={100}
            creds={creds}
          />
        </div>
      </ColumnLayout>
    </SpaceBetween>
  );
}

export default function Ec2InstanceDetail({ instance }: { instance: Ec2InstanceData }) {
  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h2">Instance Details</Header>}>
        <ColumnLayout columns={2} variant="text-grid">
          <div><Box variant="awsui-key-label">Instance ID</Box><div>{instance.instanceId}</div></div>
          <div><Box variant="awsui-key-label">Name</Box><div>{instance.name}</div></div>
          <div><Box variant="awsui-key-label">Type</Box><div>{instance.instanceType}</div></div>
          <div><Box variant="awsui-key-label">State</Box><StatusIndicator type={stateToIndicator(instance.state)}>{instance.state}</StatusIndicator></div>
          <div><Box variant="awsui-key-label">Private IP</Box><div>{instance.privateIp}</div></div>
          <div><Box variant="awsui-key-label">Public IP</Box><div>{instance.publicIp || "-"}</div></div>
          <div><Box variant="awsui-key-label">VPC</Box><div>{instance.vpcId}</div></div>
          <div><Box variant="awsui-key-label">Subnet</Box><div>{instance.subnetId}</div></div>
          <div><Box variant="awsui-key-label">Image ID</Box><div>{instance.imageId}</div></div>
          <div><Box variant="awsui-key-label">Key Name</Box><div>{instance.keyName || "-"}</div></div>
          <div><Box variant="awsui-key-label">Platform</Box><div>{instance.platform || "Linux/UNIX"}</div></div>
          <div><Box variant="awsui-key-label">Architecture</Box><div>{instance.architecture}</div></div>
          <div><Box variant="awsui-key-label">Launch Time</Box><div>{instance.launchTime}</div></div>
        </ColumnLayout>
      </Container>
      <Table
        header={<Header variant="h2">Security Groups</Header>}
        columnDefinitions={[
          { id: "GroupId", header: "GroupId", cell: (item: { GroupId: string; GroupName: string }) => item.GroupId },
          { id: "GroupName", header: "GroupName", cell: (item: { GroupId: string; GroupName: string }) => item.GroupName },
        ]}
        items={instance.securityGroups || []}
        empty="No security groups"
      />
      <Tabs
        tabs={[
          {
            id: "tags",
            label: "Tags",
            content: (
              <Table
                variant="embedded"
                columnDefinitions={[
                  { id: "Key", header: "Key", cell: (item: { Key: string; Value: string }) => item.Key },
                  { id: "Value", header: "Value", cell: (item: { Key: string; Value: string }) => item.Value },
                ]}
                items={instance.tags || []}
                empty="No tags"
              />
            ),
          },
          {
            id: "monitoring",
            label: "Monitoring",
            content: <Ec2Monitoring instanceId={instance.instanceId} />,
          },
        ]}
      />
    </SpaceBetween>
  );
}
